/*
 * REGLAS COMERCIOS NAVIMARKET 2026 en modal.
 *
 * El documento legal completo se lee sin salir de la landing: mismo mundo
 * visual (verde pino, crema, Ubuntu) pero sin animación ni decorado, medida de
 * lectura corta y jerarquía plana para que las 23 cláusulas se lean y se citen
 * sin fricción. El texto vive en src/lib/official-terms.ts y no se toca aquí.
 *
 * La modal es única y vive en App (por encima de los tabs), así que se puede
 * abrir desde cualquier panel y también con ?reglas en la URL — ese parámetro
 * es la dirección citable del documento, la que aparece en las cláusulas.
 */
import {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {LinkedText} from '@/components/LinkedText';
import {Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog';
import {OFFICIAL_TERMS, OFFICIAL_TERMS_HEADER, OFFICIAL_TERMS_OUTRO, OFFICIAL_TERMS_TITLE} from '@/lib/official-terms';

/** Parámetro que abre el documento al cargar: /?reglas (o ?reglas=1). */
export const OFFICIAL_TERMS_PARAM = 'reglas';

/** ¿La URL actual pide las reglas abiertas? Un `?reglas=0` explícito dice que no. */
function paramWantsOpen(search: string): boolean {
  const value = new URLSearchParams(search).get(OFFICIAL_TERMS_PARAM);
  if (value === null) return false;
  return value !== '0' && value.toLowerCase() !== 'false';
}

/**
 * Deja la URL contando lo que se ve, sin ensuciar el historial: quien comparte
 * la barra de direcciones con la modal abierta comparte el documento.
 */
function syncUrl(open: boolean) {
  const url = new URL(window.location.href);
  if (open) url.searchParams.set(OFFICIAL_TERMS_PARAM, '1');
  else url.searchParams.delete(OFFICIAL_TERMS_PARAM);
  history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}

const OfficialTermsContext = createContext<(() => void) | null>(null);

/** Abre la modal de reglas desde cualquier punto del árbol. */
export function useOfficialTerms(): () => void {
  const open = useContext(OfficialTermsContext);
  if (!open) throw new Error('useOfficialTerms necesita <OfficialTermsProvider>');
  return open;
}

export function OfficialTermsProvider({children}: {children: React.ReactNode}) {
  // El parámetro se lee en el primer render: si llega en la URL, la modal ya
  // monta abierta y no hay parpadeo de la landing detrás.
  const [open, setOpen] = useState(() => paramWantsOpen(window.location.search));

  useEffect(() => {
    syncUrl(open);
  }, [open]);

  // Navegación con atrás/adelante: la modal sigue a la URL.
  useEffect(() => {
    const onPopState = () => {
      setOpen(paramWantsOpen(window.location.search));
    };
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  const openTerms = useCallback(() => {
    setOpen(true);
  }, []);

  return (
    <OfficialTermsContext.Provider value={openTerms}>
      {children}
      <OfficialTermsDialog open={open} onOpenChange={setOpen} />
    </OfficialTermsContext.Provider>
  );
}

function OfficialTermsDialog({open, onOpenChange}: {open: boolean; onOpenChange: (open: boolean) => void}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel="Cerrar las reglas">
        <DialogHeader>
          <p className="overline-label text-amber">Comercios participantes</p>
          <DialogTitle className="mt-2">{OFFICIAL_TERMS_TITLE}</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <div className="border-l-2 border-amber/40 pl-4 text-sm leading-relaxed text-cream/70">
            {OFFICIAL_TERMS_HEADER.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>

          <h3 className="mt-10 text-base font-bold text-white">Términos y Condiciones</h3>
          <ol className="mt-5 list-decimal space-y-5 pl-6 text-pretty text-sm leading-relaxed text-cream/85 marker:font-medium marker:text-amber/80">
            {OFFICIAL_TERMS.map((clause, i) => (
              <li key={i} className="pl-1">
                <LinkedText text={clause} />
              </li>
            ))}
          </ol>

          <p className="mt-10 border-t border-white/10 pt-8 text-center text-sm leading-relaxed text-cream/70">
            {OFFICIAL_TERMS_OUTRO}
          </p>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
